import { useNotification } from '../../../../contexts/NotificationContext';

export interface Document {
  file_url: string;
  filename?: string;
  title: string;
  file_type?: string;
}

export const getDocumentUrl = (doc: Document): string | null => {
  if (!doc.file_url) {
    console.error('No file_url provided for document:', doc);
    return null;
  }

  // If URL is already absolute, return as is
  if (doc.file_url.startsWith('http')) {
    return doc.file_url;
  }

  // Convert relative URL to absolute URL
  // Handle both cases: URLs starting with /media/ and URLs without leading slash
  let relativeUrl = doc.file_url;
  if (!relativeUrl.startsWith('/')) {
    relativeUrl = '/' + relativeUrl;
  }

  const baseUrl = 'http://127.0.0.1:8000'; // Backend URL
  const absoluteUrl = `${baseUrl}${relativeUrl}`;

  console.log('URL conversion:', {
    original: doc.file_url,
    relative: relativeUrl,
    absolute: absoluteUrl
  });

  return absoluteUrl;
};

export const handleViewDocument = (
  doc: Document,
  notificationService: ReturnType<typeof useNotification>,
  handleDownloadDocument: (doc: Document) => void
) => {
  try {
    console.log('Attempting to view document:', doc);
    const documentUrl = getDocumentUrl(doc);
    if (!documentUrl) {
      notificationService.error('Document URL not available');
      return;
    }

    console.log('Opening document URL:', documentUrl);

    // Check if it's a viewable file type
    const viewableTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif', 'text/plain'];
    const lowerFileType = doc.file_type?.toLowerCase() || '';

    if (viewableTypes.some(type => lowerFileType.includes(type))) {
      // Open in new tab for viewable files
      const newWindow = window.open(documentUrl, '_blank');
      if (!newWindow) {
        notificationService.error('Popup blocked. Please allow popups for this site.');
      }
    } else {
      // For non-viewable files, trigger download
      handleDownloadDocument(doc);
    }
  } catch (error: any) {
    console.error('Error viewing document:', error);
    notificationService.error(error.message || 'Failed to view document');
  }
};

export const handleDownloadDocument = (
  doc: Document,
  notificationService: ReturnType<typeof useNotification>
) => {
  try {
    console.log('Attempting to download document:', doc);
    const documentUrl = getDocumentUrl(doc);
    if (!documentUrl) {
      notificationService.error('Document URL not available');
      return;
    }

    console.log('Downloading document URL:', documentUrl);

    // Create a temporary link element to trigger download
    const link = document.createElement('a');
    link.href = documentUrl;
    link.download = doc.filename || doc.title;
    link.target = '_blank';

    // Append to body, click, and remove
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    notificationService.success(`Downloading ${doc.title}`);
  } catch (error: any) {
    console.error('Error downloading document:', error);
    notificationService.error(error.message || 'Failed to download document');
  }
};

